import React, { useCallback, useEffect, useRef } from "react";

import { Typography } from "../../typography";
import css from "./multirange_slider.module.scss";

export type MultiRangeValue = {
  min: number;
  max: number;
};

type MultiRangeSliderProps = {
  min: number;
  max: number;
  onChange: ({ min, max }: MultiRangeValue) => void;
  value: MultiRangeValue;
  getValueLabel?: (count: number) => string;
  title?: string;
};

export const MultiRangeSlider = ({
  min,
  max,
  onChange,
  value,
  getValueLabel = String,
  title
}: MultiRangeSliderProps): JSX.Element => {
  const range = useRef<HTMLDivElement>(null);

  const getPercent = useCallback((value: number) => ((value - min) / (max - min)) * 100, [min, max]);

  // Set offset and width of the range
  useEffect(() => {
    const minPercent = getPercent(value.min);
    const maxPercent = getPercent(value.max);

    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [value.max, value.min, getPercent]);

  const displayedValue = `${getValueLabel(value.min)} - ${getValueLabel(value.max)}`;

  return (
    <>
      {title && (
        <Typography variant="labelRegular" className={css.title}>
          {title}
        </Typography>
      )}
      <div className={css.container}>
        <input
          type="range"
          min={min}
          max={max}
          value={value.min}
          onChange={(e) => {
            const newMinValue = Math.min(Number(e.target.value), value.max - 1);
            onChange({ min: newMinValue, max: value.max });
          }}
          className={css.thumb}
        />
        <input
          type="range"
          min={min}
          max={max}
          value={value.max}
          onChange={(e) => {
            const newMaxValue = Math.max(Number(e.target.value), value.min + 1);
            onChange({ min: value.min, max: newMaxValue });
          }}
          className={css.thumb}
        />

        <div className={css.slider}>
          <div className={css.sliderTrack} />
          <div ref={range} className={css.sliderRange} />
        </div>
      </div>
      <Typography variant="labelRegular" className={css.label}>
        {displayedValue}
      </Typography>
    </>
  );
};
